import { Typography } from '@mui/material';
import moment from 'moment';

import Routes from '@internal/config/routes';
import { Profile, Role } from '@internal/types/profile';
import {
  DealStage,
  Project,
  ProjectOfferStatus,
  ProjectPublishStatus,
} from '@internal/types/projects';

import {
  MyProjectsCategory,
  OfferStatusMapItem,
  ProjectsListQueryTab,
  ProjectsTabValue,
} from '../projects-list/projects.types';

const ProjectInvolvementTypeTitles: Readonly<Record<string, string>> = {
  FullTimeOnly: 'Full-time',
  PartTimeOnly: 'Part-time',
  BothPartAndFullTime: 'Part-time or Full-time',
  PartTimeCanSwitchToFullTime: 'Part-time, switch to full-time later',
  Unavailable: 'Unavailable',
  Unknown: 'Unknown',
};

export const projectOfferStatusMap: Record<
  ProjectOfferStatus,
  OfferStatusMapItem
> = {
  [ProjectOfferStatus.NewOffer]: {
    title: 'New offer',
    renderTitle: (firstName, lastName) => (
      <>
        New offer for{' '}
        <Typography
          variant="inherit"
          component="span"
          sx={{
            color: theme => theme.palette.info.ultraDark,
          }}
        >
          {firstName} {lastName}
        </Typography>
      </>
    ),
    infoAlertText:
      'Check out a new opportunity and let us know if you are interested.',
  },
  [ProjectOfferStatus.ApplicationSubmitted]: {
    title: 'Application submitted',
    renderTitle: (firstName, lastName) => (
      <>
        Application submitted for{' '}
        <Typography
          variant="inherit"
          component="span"
          sx={{
            color: theme => theme.palette.info.ultraDark,
          }}
        >
          {firstName} {lastName}
        </Typography>
      </>
    ),
    infoAlertText:
      'Matching team is reviewing your responses and will get back with updates soon',
  },
  [ProjectOfferStatus.WaitingForReview]: {
    title: 'Waiting for feedback',
    renderTitle: (firstName, lastName) => (
      <>
        <Typography
          variant="inherit"
          component="span"
          sx={{
            color: theme => theme.palette.info.ultraDark,
          }}
        >
          {firstName} {lastName}
        </Typography>{' '}
        Waiting for client&apos;s review
      </>
    ),
    infoAlertText:
      "We're preparing your profile for client's review, hang tight",
  },
  [ProjectOfferStatus.UnderReview]: {
    title: "Under client's review",
    renderTitle: (firstName, lastName) => (
      <>
        <Typography
          variant="inherit"
          component="span"
          sx={{
            color: theme => theme.palette.info.ultraDark,
          }}
        >
          {firstName} {lastName}
        </Typography>{' '}
        Under client&apos;s review
      </>
    ),
    infoAlertText:
      'Profile sent to the client, feedback might take a week or so, stay tuned',
  },
  [ProjectOfferStatus.OfferAccepted]: {
    title: 'Offer accepted',
    renderTitle: (firstName, lastName) => (
      <>
        Offer accepted for{' '}
        <Typography
          variant="inherit"
          component="span"
          sx={{
            color: theme => theme.palette.info.ultraDark,
          }}
        >
          {firstName} {lastName}
        </Typography>
      </>
    ),
    infoAlertText:
      'Congrats, we have a match here, a new subscription is on the way',
  },
  [ProjectOfferStatus.OfferDeclined]: {
    title: 'Offer canceled',
    renderTitle: (firstName, lastName) => (
      <>
        Offer canceled for{' '}
        <Typography
          variant="inherit"
          component="span"
          sx={{
            color: theme => theme.palette.info.ultraDark,
          }}
        >
          {firstName} {lastName}
        </Typography>
      </>
    ),
    infoAlertText:
      "The client wasn't a match for you, we've started looking for better offers",
  },
  [ProjectOfferStatus.OfferDeclinedByDev]: {
    title: 'Offer declined',
    renderTitle: (firstName, lastName) => (
      <>
        Offer declined by{' '}
        <Typography
          variant="inherit"
          component="span"
          sx={{
            color: theme => theme.palette.info.ultraDark,
          }}
        >
          {firstName} {lastName}
        </Typography>
      </>
    ),
    infoAlertText: '',
  },
  [ProjectOfferStatus.OfferDeclinedOnClientSuccessStage]: {
    title: 'Subscription closed',
    renderTitle: (firstName, lastName) => (
      <>
        Subscription closed for{' '}
        <Typography
          variant="inherit"
          component="span"
          sx={{
            color: theme => theme.palette.info.ultraDark,
          }}
        >
          {firstName} {lastName}
        </Typography>
      </>
    ),
    infoAlertText:
      'Your subscriptions with the client was closed, thanks for cooperation',
  },
};

export const projectPostedNDaysAgo = (project: Project) => {
  const daysAgo = moment().diff(moment(project.dateProjectCreated), 'days');
  return daysAgo === 0
    ? 'Posted today'
    : `Posted ${daysAgo} day${daysAgo === 1 ? '' : 's'} ago`;
};

export const projectInvolvementTypeTitle = (projectInvolvementType?: string) =>
  ProjectInvolvementTypeTitles[projectInvolvementType || 'Unknown'];

export const PROJECT_FROM_QUERY_KEY = 'from';

export const isInternalPath = (value: string): boolean =>
  value.startsWith('/') && !value.startsWith('//');

export const getProjectHref = (project: Project, from: string): string => {
  const developer = project.partnerDeveloper?.id
    ? `/${project.partnerDeveloper.id}`
    : '';
  const query = isInternalPath(from)
    ? `?${PROJECT_FROM_QUERY_KEY}=${encodeURIComponent(from)}`
    : '';
  return `${Routes.ViewProject}/${project.dealId}${developer}${query}`;
};

export const getProjectBackTarget = (
  from: string | string[] | undefined,
  role?: Role
): { href: string; label: string } => {
  const value = Array.isArray(from) ? from[0] : from;
  if (role === 'partner manager') {
    return {
      href: value?.startsWith(Routes.MyApplications)
        ? value
        : Routes.MyApplications,
      label: 'Back to applications',
    };
  }
  if (role === 'partner developer') {
    return { href: Routes.Profile, label: 'Back to profile' };
  }
  if (value && isInternalPath(value)) {
    return {
      href: value,
      label: value.startsWith(Routes.MyApplications)
        ? 'Back to applications'
        : 'Back to projects',
    };
  }
  return { href: Routes.OpenProjects, label: 'Back to projects' };
};

export const isProjectClosed = (project: Project): boolean =>
  typeof project.isSearchOpen === 'boolean'
    ? !project.isSearchOpen
    : project.dealStage === DealStage.ClosedLost ||
      project.dealStage === DealStage.ClosedWon;

export const isProjectOpen = (project: Project): boolean =>
  project.projectPublishStatus === ProjectPublishStatus.Published &&
  !isProjectClosed(project);

export const hasValidHiringPosition = (project: Project): boolean =>
  Boolean(project.hiringPositionId);

export const canSelfApplyTo = (project: Project): boolean =>
  isProjectOpen(project) && hasValidHiringPosition(project);

export const getProjectOfferStatus = (
  project: Project
): ProjectOfferStatus | null => {
  if (!project.offerStatus) return null;
  return project.offerStatus === ProjectOfferStatus.NewOffer &&
    project.hasCandidateConfirmedApplication
    ? ProjectOfferStatus.ApplicationSubmitted
    : project.offerStatus;
};

const ENDED_OFFER_STATUSES: ReadonlySet<ProjectOfferStatus> = new Set([
  ProjectOfferStatus.OfferDeclined,
  ProjectOfferStatus.OfferDeclinedByDev,
  ProjectOfferStatus.OfferDeclinedOnClientSuccessStage,
]);

export interface ProjectStatusChip {
  label: string;
  ended: boolean;
}

export const getProjectStatusChip = (
  project: Project
): ProjectStatusChip | null => {
  const offerStatus = getProjectOfferStatus(project);
  if (!offerStatus) return null;
  return {
    label: projectOfferStatusMap[offerStatus].title,
    ended: ENDED_OFFER_STATUSES.has(offerStatus),
  };
};

export const getProjectsListTabsValues = (
  queryTab: string
): {
  tabValue: ProjectsTabValue;
  myProjectsCategory: MyProjectsCategory;
} | null => {
  if (queryTab === ProjectsListQueryTab.MY_PROJECTS_CURRENT_OFFERS) {
    return {
      tabValue: ProjectsTabValue.MY_PROJECTS,
      myProjectsCategory: MyProjectsCategory.CURRENT_OFFERS,
    };
  }

  if (queryTab === ProjectsListQueryTab.MY_PROJECTS_ACCEPTED) {
    return {
      tabValue: ProjectsTabValue.MY_PROJECTS,
      myProjectsCategory: MyProjectsCategory.ACCEPTED,
    };
  }

  if (queryTab === ProjectsListQueryTab.MY_PROJECTS_CANCELED) {
    return {
      tabValue: ProjectsTabValue.MY_PROJECTS,
      myProjectsCategory: MyProjectsCategory.CANCELED,
    };
  }

  if (queryTab === ProjectsListQueryTab.HOT_PROJECTS) {
    return {
      tabValue: ProjectsTabValue.HOT_PROJECTS,
      myProjectsCategory: MyProjectsCategory.CURRENT_OFFERS,
    };
  }

  if (queryTab === ProjectsListQueryTab.RECENTLY_CLOSED) {
    return {
      tabValue: ProjectsTabValue.RECENTLY_CLOSED,
      myProjectsCategory: MyProjectsCategory.CURRENT_OFFERS,
    };
  }

  return null;
};

export const getQueryTab = ({
  tabValue,
  myProjectsCategory,
}: {
  tabValue: ProjectsTabValue;
  myProjectsCategory: MyProjectsCategory;
}): string => {
  if (
    tabValue === ProjectsTabValue.MY_PROJECTS &&
    myProjectsCategory === MyProjectsCategory.CURRENT_OFFERS
  ) {
    return ProjectsListQueryTab.MY_PROJECTS_CURRENT_OFFERS;
  }

  if (
    tabValue === ProjectsTabValue.MY_PROJECTS &&
    myProjectsCategory === MyProjectsCategory.ACCEPTED
  ) {
    return ProjectsListQueryTab.MY_PROJECTS_ACCEPTED;
  }

  if (
    tabValue === ProjectsTabValue.MY_PROJECTS &&
    myProjectsCategory === MyProjectsCategory.CANCELED
  ) {
    return ProjectsListQueryTab.MY_PROJECTS_CANCELED;
  }

  if (tabValue === ProjectsTabValue.HOT_PROJECTS) {
    return ProjectsListQueryTab.HOT_PROJECTS;
  }

  if (tabValue === ProjectsTabValue.RECENTLY_CLOSED) {
    return ProjectsListQueryTab.RECENTLY_CLOSED;
  }

  return '';
};

const redirects: Partial<
  Record<
    Profile['role'],
    Array<{ from: ProjectsTabValue; to: ProjectsTabValue }>
  >
> = {
  candidate: [
    {
      from: ProjectsTabValue.MY_PROJECTS,
      to: ProjectsTabValue.HOT_PROJECTS,
    },
  ],
  'partner developer': [
    {
      from: ProjectsTabValue.MY_PROJECTS,
      to: ProjectsTabValue.HOT_PROJECTS,
    },
  ],
  developer: [
    {
      from: ProjectsTabValue.MY_PROJECTS,
      to: ProjectsTabValue.HOT_PROJECTS,
    },
  ],
};

export const redirectsByRole = ({
  role,
  navigation,
}: {
  role: Role;
  navigation: ProjectsTabValue;
}) => {
  const redirect = redirects[role]?.find(({ from }) => from === navigation);

  return redirect ? redirect.to : navigation;
};
